import { useGameStore } from '@/store/gameStore'
import { useCareerStore } from '@/store/careerStore'
import { acceptClubOffer } from '@/game/systems/careerActions'
import { SectionDeck, EmptyStateRich, IconTile } from '../components/CourtVision'
import { useConfirm } from '../components/useConfirm'
import { BroadcastHeader, TrophyCabinet, AngledButton } from '../components/broadcast'
import { seasonsToTrophyItems } from '../helpers/palmares'
import { HIGHEST_DIVISION } from '@/config/leagueConfig'
import { Briefcase, History, Landmark, Target, Mail } from 'lucide-react'

export function CareerTabContent() {
  const leagueHistory = useGameStore(s => s.leagueHistory)
  const boardConfidence = useGameStore(s => s.boardConfidence)
  const seasonObjective = useGameStore(s => s.seasonObjective)
  const league = useGameStore(s => s.league)
  const { managerReputation, currentClubName, clubHistory, pendingOffers, clearOffers } = useCareerStore()
  const { confirm, confirmDialog } = useConfirm()

  const handleAcceptOffer = async (offerId: string, clubName: string) => {
    if (await confirm({
      title: 'Aceptar nuevo cargo',
      icon: <Briefcase size={20} />,
      message: <>¿Aceptar el cargo en <b>{clubName}</b>? Tu club actual quedará archivado en tu historial de carrera y empezarás un proyecto nuevo.</>,
      confirmLabel: 'Aceptar cargo',
    })) {
      acceptClubOffer(offerId)
    }
  }

  const clubName = currentClubName || 'Tu club'
  const confidenceTone = boardConfidence >= 60 ? 'success' : boardConfidence >= 30 ? 'warning' : 'danger'

  // Career timeline: archived clubs first (oldest), current club last (newest)
  const archivedItems = clubHistory.flatMap(c =>
    seasonsToTrophyItems(c.leagueHistory, { idPrefix: `arch-${c.clubName}`, clubName: c.clubName })
  )
  const currentItems = seasonsToTrophyItems(leagueHistory, { idPrefix: 'cur', clubName })
  const timeline = [...currentItems, ...archivedItems]

  const totalTitles =
    leagueHistory.filter(h => h.position === 1 && h.division === HIGHEST_DIVISION).length +
    clubHistory.reduce((sum, c) => sum + c.titles, 0)
  const seasonsManaged =
    leagueHistory.length + clubHistory.reduce((sum, c) => sum + c.seasonsManaged, 0)

  return (
    <div>
      {confirmDialog}
      <BroadcastHeader
        title={clubName}
        competition="Mánager"
        icon={Briefcase}
        meta={
          <>
            <span className="chip-angled tone-gold">Prestigio {Math.round(managerReputation)}</span>
            <span className="chip-angled">{seasonsManaged} temporada{seasonsManaged === 1 ? '' : 's'}</span>
            {totalTitles > 0 && <span className="chip-angled">{totalTitles} título{totalTitles === 1 ? '' : 's'}</span>}
            {clubHistory.length > 0 && (
              <span className="chip-angled">{clubHistory.length + 1} clubes</span>
            )}
          </>
        }
      />

      {pendingOffers.length > 0 && (
        <SectionDeck
          title="Ofertas de otros clubes"
          icon={Mail}
          meta={`${pendingOffers.length} sobre la mesa`}
          action={
            <AngledButton tone="neutral" onClick={clearOffers}>Rechazar todas</AngledButton>
          }
        >
          <div className="space-y-2">
            {pendingOffers.map(offer => (
              <div key={offer.id} className="row-tile tx-perforations">
                <IconTile icon={Briefcase} tone="cta" />
                <div className="row-tile-main">
                  <div className="row-tile-title">{offer.clubName}</div>
                  <div className="row-tile-sub">{offer.description} · {offer.startingMoney.toLocaleString('es-ES')}€ de partida</div>
                </div>
                <AngledButton tone="gold" onClick={() => handleAcceptOffer(offer.id, offer.clubName)}>
                  Aceptar
                </AngledButton>
              </div>
            ))}
          </div>
        </SectionDeck>
      )}

      <SectionDeck title="Junta directiva" icon={Landmark} meta={league ? undefined : 'Sin liga activa'}>
        <div className="row-tile" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Target size={15} style={{ color: 'var(--gold)' }} />
            <span className="row-tile-title" style={{ flex: 1 }}>
              {seasonObjective ? seasonObjective.label : 'Sin objetivo de temporada'}
            </span>
            {seasonObjective && <span className="chip-angled tone-gold">Objetivo</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span className="meta-text" style={{ minWidth: '70px' }}>Confianza</span>
            <div className="progress-track" style={{ flex: 1 }}>
              <div
                className={`progress-fill angled ${confidenceTone}`}
                style={{ width: `${boardConfidence}%` }}
              />
            </div>
            <span className="meta-text" style={{ minWidth: '36px', textAlign: 'right', fontWeight: 700 }}>
              {Math.round(boardConfidence)}%
            </span>
          </div>
        </div>
      </SectionDeck>

      <SectionDeck
        title="Línea temporal"
        icon={History}
        meta={timeline.length > 0 ? `${timeline.length} temporada${timeline.length === 1 ? '' : 's'}` : undefined}
      >
        {timeline.length === 0 ? (
          <EmptyStateRich
            icon={History}
            title="Tu carrera acaba de empezar"
            sub="Inscríbete en la liga y compite temporada a temporada: aquí quedará registrada tu trayectoria"
          />
        ) : (
          <TrophyCabinet items={timeline} />
        )}
      </SectionDeck>
    </div>
  )
}
